import { dehydrate, QueryClient } from '@tanstack/react-query';
import React from 'react';
import Fade from 'react-reveal/Fade';

import { Globe } from '@/components/Core/Globe/Globe';
import SectionTitle from '@/components/Core/SectionTitle/SectionTitle';
import type { ContactFormData } from '@/components/Forms/ContactForm/ContactForm';
import ContactForm from '@/components/Forms/ContactForm/ContactForm';
import MainLayout from '@/components/Layout/Main';
import { CustomMeta } from '@/components/Meta/CustomMeta';
import SmartLink from '@/components/Primitive/SmartLink';
import useQuery, { makeQuery } from '@/hooks/use-query';
import {
  createContactPageQuery,
  mapToContactPage,
} from '@/queries/globals/contact';

import postApi from '../src/lib/api-poster';

const contactPageQuery = createContactPageQuery();
const contactPageVars = {};

const ContactPage = () => {
  const { data } = useQuery(
    contactPageQuery,
    contactPageVars,
    mapToContactPage
  );
  const { ctaBlocks, locations } = data || {};

  const [formError, setFormError] = React.useState('');
  const [formSuccess, setFormSuccess] = React.useState(false);
  const [formSubmitting, setFormSubmitting] = React.useState(false);

  const onSubmit = async (formData: ContactFormData) => {
    setFormError('');
    setFormSuccess(false);
    setFormSubmitting(true);
    const res = await postApi('/api/contact', formData);
    if (res.status !== 200) {
      setFormError(
        'There was an error submitting your message. Please try again later.'
      );
    } else {
      setFormSuccess(true);
    }
    setFormSubmitting(false);
  };

  return (
    <MainLayout withNavSpacer>
      <CustomMeta {...data?.seo} />
      <div className="pb-page space-y-page mx-auto w-full overflow-hidden">
        <div className="container-lg space-y-8 md:space-y-12">
          <SectionTitle className="pt-3" title="CONTACT" />
          <Fade cascade bottom>
            <div className="flex flex-wrap items-stretch justify-center gap-5 gap-y-10 md:gap-10 ">
              {ctaBlocks?.map(
                ({ id, title, description, ctaLabel, ctaHref }) => (
                  <SmartLink
                    key={id}
                    {...(ctaHref && { href: ctaHref })}
                    className="group relative flex basis-[349px] flex-col items-center justify-center self-stretch rounded-md bg-blackAlt/40 p-5 py-10 text-center"
                  >
                    <div className="absolute left-0 top-0 z-[1] h-full w-full rounded-md opacity-0 transition-all group-hover:opacity-100 group-focus:opacity-100">
                      <div className="absolute left-[-2px] top-[-2px] h-[calc(100%+4px)] w-[calc(100%+4px)] rounded-md border-8 border-neonBlue-500 blur-[3px] transition-all" />
                      <div className="absolute h-full w-full rounded-md border-4 border-white transition-all" />
                    </div>

                    <h2 className="pb-2 font-dharma text-5xl font-black uppercase italic leading-none text-cream md:text-5xl">
                      {title}
                    </h2>
                    <p className="text-xl text-grayAlt-500">{description}</p>
                    {ctaLabel && (
                      <div className="z-10 mt-auto pt-4 font-bold  uppercase tracking-wide text-cream underline transition-colors group-hover:text-pink-500 group-focus:text-pink-500 md:pt-12 md:text-xl">
                        {ctaLabel}
                      </div>
                    )}
                  </SmartLink>
                )
              )}
            </div>
          </Fade>
        </div>

        <div className="container-md flex flex-col items-center justify-center space-y-1">
          <h2 className="pb-6 text-center text-2xl font-black uppercase text-cream md:text-3xl">
            Get in touch
          </h2>
          <ContactForm
            error={formError}
            isSuccess={formSuccess}
            onSubmit={onSubmit}
            isLoading={formSubmitting}
          />
        </div>

        {locations && (
          <div className="flex flex-col items-center justify-center space-y-1">
            <SectionTitle intro="WHERE YOU'LL" title="FIND US" />
            <Globe locations={locations.map((l) => l.coordinates)} />
            <div className="max-w-[450px] text-center md:max-w-xs md:text-3xl">
              {locations.map((l) => (
                <p
                  className="text-2xl font-black uppercase text-cream"
                  key={l.title}
                >
                  {l.title}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ContactPage;

export async function getStaticProps() {
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery(
    ['query', contactPageQuery, contactPageVars],
    async () => {
      return makeQuery(contactPageQuery, contactPageVars, mapToContactPage);
    }
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60,
  };
}
